import { useCallback, useState } from 'react';

/**
 * Hook for managing expandable section state using Set-based expansion.
 *
 * Note: Multiple simultaneous expansions are supported. When multiple items
 * are expanded, focus moves to the most recently expanded item's content.
 *
 * @returns {Object} Hook state and actions
 * @returns {Set<string>} expandedIds - Set of currently expanded item IDs
 * @returns {Function} toggleExpanded - Toggle expansion state for an item ID
 * @returns {Function} expand - Add an item ID to the expanded set (no-op if already expanded)
 * @returns {Function} isExpanded - Check if an item ID is expanded
 */
export function useExpandableSection() {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expand = useCallback((id: string) => {
    setExpandedIds(prev => {
      if (prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds]
  );

  return {
    expandedIds: expandedIds as ReadonlySet<string>,
    toggleExpanded,
    expand,
    isExpanded,
  };
}
